import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";

const API = "https://eps.elkomprintama.com/";

const TABS = ["Operator", "QC"];

const inp =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-400";

function PersonTable({ rows, onToggleActive, label }) {
  const idKey = label === "QC" ? "qc_id" : "opr_id";
  const nameKey = label === "QC" ? "qc_name" : "opr_name";

  // Hide the "Unassigned" placeholder entry (id=1)
  const visible = rows.filter((r) => r[idKey] !== 1);

  if (visible.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 italic text-sm">
        No {label.toLowerCase()} entries yet.
      </div>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 sticky top-0 z-10">
        <tr>
          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
            No
          </th>
          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
            Name
          </th>
          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
            Status
          </th>
          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
            Toggle
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {visible.map((r, i) => {
          const isActive = r.active === 1 || r.active === true;
          return (
            <tr
              key={r[idKey]}
              className={
                i % 2 === 0
                  ? "bg-white hover:bg-green-50"
                  : "bg-gray-50 hover:bg-green-50"
              }
            >
              <td className="px-4 py-2.5 text-gray-400 w-12">{i + 1}</td>
              <td className="px-4 py-2.5 font-medium text-gray-800">
                {r[nameKey]}
              </td>
              <td className="px-4 py-2.5 text-center">
                <span
                  className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    isActive
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {isActive ? "Active" : "Inactive"}
                </span>
              </td>
              <td className="px-4 py-2.5 text-center">
                <button
                  onClick={() => onToggleActive(r[idKey], !isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    isActive ? "bg-green-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      isActive ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function OperatorQCManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [operators, setOperators] = useState([]);
  const [qcs, setQcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpr, setNewOpr] = useState("");
  const [newQc, setNewQc] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [oprRes, qcRes] = await Promise.all([
        axios.get(`${API}get/api/opr/all`, { withCredentials: true }),
        axios.get(`${API}get/api/qc/all`, { withCredentials: true }),
      ]);
      setOperators(oprRes.data.result || []);
      setQcs(qcRes.data.result || []);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleAddOperator = async (e) => {
    e.preventDefault();
    if (!newOpr.trim()) {
      toast.warning("Enter operator name.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}post/api/opr`,
        { opr_name: newOpr },
        { withCredentials: true },
      );
      toast.success("Operator added!");
      setNewOpr("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddQc = async (e) => {
    e.preventDefault();
    if (!newQc.trim()) {
      toast.warning("Enter QC name.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}post/api/qc`,
        { qc_name: newQc },
        { withCredentials: true },
      );
      toast.success("QC added!");
      setNewQc("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOpr = async (id, active) => {
    try {
      await axios.patch(
        `${API}patch/api/opr/${id}/active`,
        { active },
        { withCredentials: true },
      );
      setOperators((prev) =>
        prev.map((o) =>
          o.opr_id === id ? { ...o, active: active ? 1 : 0 } : o,
        ),
      );
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const handleToggleQc = async (id, active) => {
    try {
      await axios.patch(
        `${API}patch/api/qc/${id}/active`,
        { active },
        { withCredentials: true },
      );
      setQcs((prev) =>
        prev.map((q) =>
          q.qc_id === id ? { ...q, active: active ? 1 : 0 } : q,
        ),
      );
    } catch {
      toast.error("Failed to update status.");
    }
  };

  return (
    <>
      <div className="w-full rounded-lg bg-green-600 mb-4">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Operator &amp; QC
        </label>
      </div>

      <div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative flex flex-col"
        style={{ maxHeight: "calc(100vh - 160px)" }}
      >
        {loading && <LoadingOverlay message="Loading..." />}

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-4 pt-3 gap-1 bg-gray-50 flex-shrink-0">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(i)}
              className={`px-6 py-2 text-sm font-semibold rounded-t-lg transition focus:outline-none ${
                activeTab === i
                  ? "bg-green-600 text-white"
                  : "text-gray-500 hover:text-green-600 hover:bg-green-50"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Add form */}
        <div className="px-5 py-4 border-b border-gray-100 bg-green-50 flex-shrink-0">
          <form
            onSubmit={activeTab === 0 ? handleAddOperator : handleAddQc}
            className="flex gap-2 items-end"
          >
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                Add New {activeTab === 0 ? "Operator" : "QC"}
              </label>
              {activeTab === 0 ? (
                <input
                  className={`w-full ${inp}`}
                  name="newopr" type="text"
                  placeholder="e.g. Ahmad Bin Ali"
                  value={newOpr}
                  onChange={(e) => setNewOpr(e.target.value)}
                />
              ) : (
                <input
                  className={`w-full ${inp}`}
                  name="newqc" type="text"
                  placeholder="e.g. Siti Binti Rahman"
                  value={newQc}
                  onChange={(e) => setNewQc(e.target.value)}
                />
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 transition"
            >
              {saving ? "Adding..." : "Add"}
            </button>
          </form>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          {activeTab === 0 ? (
            <PersonTable
              rows={operators}
              onToggleActive={handleToggleOpr}
              label="Operator"
            />
          ) : (
            <PersonTable
              rows={qcs}
              onToggleActive={handleToggleQc}
              label="QC"
            />
          )}
        </div>
      </div>
    </>
  );
}

export default OperatorQCManagement;
